import equal from 'fast-deep-equal'
import { useEffect, useRef } from 'react'

import { ResourceType } from '../types'

interface PromiseCache {
  promise: Promise<void>
  inputs: any
  error?: any
  response?: any
}

const promisesCache: { [resourceType: string]: { [cacheId: string]: { [inputId: string]: PromiseCache } } } = {}

/**
 * Hook manages the relationship between a promise and <Suspense />.
 *
 * What it does:
 * - Throws a promise which will be caught by a parent <Suspense />
 * - Creates a cache reference of Promise + Inputs + Resource type which can be recalled when this is re-run
 * - When the promise resolves, the <Suspense /> component will re-render it's children
 * - This hook will re-run, but the reference to the cache reference will return the result of the promise (instead of throwing)
 *
 * Why the complicated cache?
 * - The Suspense pattern relies on 2 things:
 *    - A component using this hook should not even render if the promise hasn't been resolved
 *    - A component should not need to have guards in place to check for null / undefined if the promise is not resolved
 *
 * Since useEffect and other hooks will run after a component is mounted, the only way to support the suspense pattern is to
 * manage a cache "outside" of the render lifecycle - which ensures the promise is checked/thrown before any components are
 * mounted.
 *
 * Managing a Global Cache means:
 * - We need to ensure that cache is cleaned up (i.e. if component unmounts, the cache reference removed)
 *
 * By tracking unique references of Promise + Inputs + Resource to identify a cache we will ensure a component will not
 * make a network request (and wil instead return the response) if the all conditions remain the same. A cache can be inactivated if...
 * - A component changes its "Inputs" - This will result in throwing the old promise out, and creating a new one
 * - A component is unmounted - This will also result in the Promise + Inputs + resourceType combo being thrown out
 *
 * @param promise promise which will be thrown to Suspense - which will hold until promise resolves
 * @param inputs arguments to be passed along with your promise
 * @param resourceType resourceType of the associated promise
 * @returns response of promise
 */
export default function useSuspense<Args extends any, Result>(
  promise: (inputs: Args) => Promise<Result>,
  inputs: Args,
  resourceType: ResourceType
): Result {
  const prevArgs = useRef<string | null>(null)
  const cacheKey = String(promise)
  const inputsKey = JSON.stringify(inputs)
  const cachedPromise: PromiseCache | undefined =
    promisesCache[resourceType] &&
    promisesCache[resourceType][cacheKey] &&
    promisesCache[resourceType][cacheKey][inputsKey]

  useEffect(() => {
    // When mounting for the first time, add a subscription
    if (prevArgs.current !== inputsKey) {
      prevArgs.current = inputsKey
    }

    // When unmounting of state changing, decrement subs or remove cache completely
    return () => {
      if (cachedPromise) {
        delete promisesCache[resourceType][cacheKey][inputsKey]
      }
    }
  }, [cacheKey, inputsKey])

  if (cachedPromise) {
    if (equal(inputs, cachedPromise.inputs)) {
      // If an error occurred,
      if (Object.prototype.hasOwnProperty.call(cachedPromise, 'error')) {
        throw cachedPromise.error
      }

      // If a response was successful,
      else if (Object.prototype.hasOwnProperty.call(cachedPromise, 'response')) {
        return cachedPromise.response
      }

      throw cachedPromise.promise
    }
  }

  // The request is new or has changed.
  const promiseCache: PromiseCache = {
    promise:
      // Make the promise request.
      promise(inputs)
        .then((response: Result) => {
          promiseCache.response = response
        })
        .catch((e: Error) => {
          promiseCache.error = e
        }),
    inputs,
  }

  promisesCache[resourceType] = {
    ...promisesCache[resourceType],
    [cacheKey]: {
      ...promisesCache[resourceType]?.[cacheKey],
      [inputsKey]: promiseCache,
    },
  }

  throw promiseCache.promise
}
